import React, { useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { JURISDICTIONS, jurisdictionName } from '../jurisdictions';

/**
 * .pen "Component—State select (Web)" (`Teer1`): a surface field carrying an
 * uppercase muted label and the chosen value, opening a LEDGE panel of options
 * (selected row = brand tint + check).
 *
 * The .pen shows four options because a mock cannot show 51; a real list of 51
 * needs a filter, so the panel opens with one. Everything else is the frame.
 * Small and self-contained on purpose: Jordan has not reviewed these frames
 * yet, so restyling must not mean rewriting the step.
 */
export interface StateSelectProps {
  value?: string;
  onChange: (code: string) => void;
  label?: string;
  testId?: string;
}

export const StateSelect: React.FC<StateSelectProps> = ({
  value,
  onChange,
  label = 'Where you are getting licensed',
  testId = 'licensing-state-select',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return JURISDICTIONS;
    return JURISDICTIONS.filter(
      (j) => j.name.toLowerCase().includes(q) || j.code.toLowerCase() === q
    );
  }, [query]);

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          setQuery('');
        }}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 bg-white text-left transition-colors ${
          open ? 'border-primary-500' : 'border-neutral-200 hover:border-primary-300'
        }`}
      >
        <span className="min-w-0">
          <span
            className={`block text-xs font-semibold uppercase tracking-wider ${
              open ? 'text-primary-600' : 'text-neutral-500'
            }`}
          >
            {label}
          </span>
          <span
            className={`block truncate text-base font-semibold ${
              value ? 'text-neutral-900' : 'text-neutral-400'
            }`}
          >
            {value ? jurisdictionName(value) : 'Choose your state'}
          </span>
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 transition-transform ${open ? 'rotate-180 text-primary-600' : 'text-neutral-400'}`}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="listbox"
          aria-label="State"
          data-testid={`${testId}-panel`}
          className="absolute z-20 mt-2 w-full rounded-2xl border-2 border-neutral-200 bg-white shadow-ledge overflow-hidden"
        >
          <div className="p-2 border-b border-neutral-200">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a state"
              data-testid={`${testId}-search`}
              className="w-full px-3 py-2 text-sm rounded-lg border-2 border-neutral-200 bg-neutral-100 text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-2 space-y-1">
            {options.length === 0 && (
              <p className="px-3 py-2 text-sm text-neutral-500">No state matches that.</p>
            )}
            {options.map((option) => {
              const selected = option.code === value;
              return (
                <button
                  key={option.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-testid={`licensing-state-${option.code}`}
                  onClick={() => {
                    onChange(option.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-colors ${
                    selected
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-neutral-800 hover:bg-neutral-100'
                  }`}
                >
                  <span>{option.name}</span>
                  {selected && <Check className="w-4 h-4 text-primary-600" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StateSelect;
